import {
	CheckboxField,
	CreateDrawerFormProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	SingleSelectField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	useUserPermission,
	validateEachLanguage,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { contactFilterService } from "../shared/api/contactFilterService";
import {
	ContactFilterFormValues,
	ContactFilterItemFormValues,
	ContactFilterTranslationEntry,
	ContactFilterType,
} from "../shared/types";
import { ContactFilterDrawerFilterItems } from "./ContactFilterDrawerFilterItems";
import { mapResourceToFormValues } from "./mappers";

type OptionTranslationMap = { [K in ContactFilterType]?: string };

export const useContactFilterValidation = ({ requiresNameField }: { requiresNameField: boolean }) => {
	const { t } = useTranslation();
	return (formValues: ContactFilterFormValues) => {
		const errors: { [K in keyof ContactFilterFormValues]?: any } = {};

		formValues.filterItems.forEach((filterItem, index) => {
			const error: { [K in keyof ContactFilterItemFormValues]?: any } = {};

			if (!filterItem.operator) {
				error.operator = t("Required field");
			}

			if (
				filterItem.operator &&
				filterItem.operator !== "IS_EMPTY" &&
				filterItem.operator !== "IS_NOT_EMPTY" &&
				(filterItem.value == null || filterItem.value === "")
			) {
				error.value = t("Required field");
			}

			if (Object.keys(error).length) {
				errors.filterItems = { ...errors.filterItems, [index]: error };
			}
		});

		return {
			...validateEachLanguage(formValues.translations, (_, values) => {
				const errors: { [K in keyof ContactFilterTranslationEntry]?: any } = {};

				if (requiresNameField && !values.name) {
					errors.name = t("Required field");
				}
				return errors;
			}),
			...errors,
		};
	};
};

const emptyTranslation = { name: "" };

export const ContactFilterCreateDrawer: FC<CreateDrawerFormProps<ContactFilterFormValues>> = (props) => {
	const validate = useContactFilterValidation({ requiresNameField: true });
	const { languageSettings } = useDynamicConfig();
	const { t } = useTranslation();
	const { permissions } = useUserPermission();
	const contactFilterTypeOptions: OptionTranslationMap = {
		personal: t("contactFilterType_personal"),
		...(permissions.find((x) => x.name === "contact.publicFilter.create")
			? { public: t("contactFilterType_public") }
			: {}),
		...(permissions.find((x) => x.name === "contact.systemFilter.create")
			? {
					system: t("contactFilterType_system"),
				}
			: {}),
	};
	const initialValues: ContactFilterFormValues = useMemo(
		() => ({
			translations: [{ locale: languageSettings.fallback, name: "" }],
			type: "personal",
			filterItems: [],
			showInSidebar: false,
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create contact filter")}
			validate={validate}
			renderForm={({ formik: { values, setFieldValue }, ...props }) => (
				<I18nFormDrawerContent {...props} emptyTranslation={{ name: "" }}>
					<I18nTextField label={t("Name")} name="name" required={true} />
					<I18nOnlyOnMainForm>
						<SingleSelectField
							name="type"
							label={t("Type")}
							options={Object.keys(contactFilterTypeOptions) as ContactFilterType[]}
							getLabel={(x) => t(contactFilterTypeOptions[x] as string)}
							clearable={false}
							onChange={(e) => {
								if ((e.target.value as ContactFilterType) !== "system") {
									void setFieldValue("showInSidebar", false);
								}
							}}
						/>
						{values.type === "system" && <CheckboxField label={t("Show in sidebar")} name="showInSidebar" />}
						<ContactFilterDrawerFilterItems />
					</I18nOnlyOnMainForm>
				</I18nFormDrawerContent>
			)}
		/>
	);
};

export const ContactFilterUpdateDrawer: FC<UpdateDrawerFormProps<ContactFilterFormValues>> = (props) => {
	const validate = useContactFilterValidation({ requiresNameField: true });
	const { t } = useTranslation();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit contact filter")}
			validate={validate}
			queryFn={() => contactFilterService.show(props.id).then((x) => mapResourceToFormValues(x.data.data))}
			queryKey={[...contactFilterService.showKey(props.id), "contact-filter-update-drawer"]}
			renderSuccess={(props) => {
				const contactFilterTypeOptions: OptionTranslationMap = {
					personal: t("contactFilterType_personal"),
					...(props.formik.initialValues.type === "personal"
						? {
								public: t("contactFilterType_public"),
							}
						: {}),
				};

				return (
					<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
						<I18nTextField label={t("Name")} name="name" required={true} />
						<I18nOnlyOnMainForm>
							{props.formik.initialValues.type === "personal" && (
								<SingleSelectField
									name="type"
									label={t("Type")}
									options={Object.keys(contactFilterTypeOptions) as ContactFilterType[]}
									getLabel={(x) => t(contactFilterTypeOptions[x] as string)}
									clearable={false}
								/>
							)}
							{props.formik.values.type === "system" && (
								<CheckboxField label={t("Show in sidebar")} name="showInSidebar" />
							)}
							<ContactFilterDrawerFilterItems />
						</I18nOnlyOnMainForm>
					</I18nFormDrawerContent>
				);
			}}
		/>
	);
};
